'use client'
import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import OtpInput from './OtpInput'
import type { AuthView } from './LoginSignupIndex'
import { useUserResendOtpMutation, useUserVerifyOtpMutation } from '@/src/store/rtkQueries/userAuthApi'

const OTP_LENGTH = 6
const RESEND_COOLDOWN = 60

/** Matches `/user/verify` — use `forgot_password` if your API expects that instead of `verify`. */
const FORGOT_OTP_VERIFY_TYPE = 'verify' as const

interface ForgotVerifyOtpProps {
    navigate: (view: AuthView) => void
    email?: string
    onTokenReceived: (token: string) => void
}

export default function ForgotVerifyOtp({ navigate, email, onTokenReceived }: ForgotVerifyOtpProps) {
    const [otpValue, setOtpValue] = useState('')
    const [userVerifyOtp, { isLoading: isVerifying }] = useUserVerifyOtpMutation()
    const [resendOtp, { isLoading: isResending }] = useUserResendOtpMutation()
    const [cooldown, setCooldown] = useState(RESEND_COOLDOWN)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
        return () => clearInterval(timerRef.current!)
    }, [])

    const startCooldown = () => {
        setCooldown(RESEND_COOLDOWN)
        clearInterval(timerRef.current!)
        timerRef.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const canVerify = otpValue.length === OTP_LENGTH

    const handleResend = async () => {
        try {
            const response = await resendOtp({ email })
            if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                toast.success(response?.data?.message || 'A new code was sent')
                setOtpValue('')
                startCooldown()
            }
        } catch (error) {
            console.error('resend forgot OTP', error)
        }
    }

    const handleVerify = async () => {
        if (!canVerify) return
        try {
            const response = await userVerifyOtp({
                email,
                code: otpValue,
                type: FORGOT_OTP_VERIFY_TYPE,
            })
            if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                toast.success(response?.data?.message || 'OTP verified successfully')
                onTokenReceived(response?.data?.data?.token)
                navigate('ResetPassword')
            }
        } catch (error) {
            console.error('verify forgot OTP', error)
        }
    }

    return (
        <div className="flex flex-col gap-6 w-full">
            {/* Heading */}
            <div className="space-y-2 text-body-color">
                <h1 className="secondary_heading">
                    Forgot <span className="font-secondary italic">Password?</span>
                </h1>
                <p className="text-sm">
                    By creating an account, I am also consenting to receive SMS, messages and emails.
                </p>
                {email ? (
                    <p className="text-sm text-body-color/70">
                        We sent a code to <span className="font-medium text-body-color">{email}</span>
                    </p>
                ) : null}
            </div>

            {/* OTP */}
            <div className="space-y-3">
                <p className="form_label">Enter Verification Code</p>
                <OtpInput
                    value={otpValue}
                    onChange={setOtpValue}
                    length={OTP_LENGTH}
                    onComplete={handleVerify}
                    classNames={{ wrapper: "flex gap-3" }}
                    ariaLabelPrefix="Digit"
                />
            </div>

            {/* Resend */}
            <p className="text-sm text-body-color/60">
                Didn&apos;t get your code?{' '}
                {cooldown > 0 ? (
                    <span className="text-body-color/40">Resend in {cooldown}s</span>
                ) : (
                    <button
                        type="button"
                        className="accent_text disabled:opacity-50"
                        disabled={isResending || !email}
                        onClick={handleResend}
                    >
                        {isResending ? 'Sending...' : 'Send a new code'}
                    </button>
                )}
            </p>

            {/* Verify */}
            <Button
                type="button"
                isDisabled={!canVerify || isVerifying || !email}
                onClick={handleVerify}
                className="global_btn rounded_full w-full text-sm font-medium tracking-wide mt-auto"
            >
                Verify
            </Button>

            {/* Back to sign in */}
            <p className="text-sm text-center w-full text-body-color">
                Remember it?{' '}
                <span onClick={() => navigate('SignIn')} className="accent_text">
                    Sign in
                </span>
            </p>
        </div>
    )
}
